import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import EditableText from "./EditableText";
import { RowActions, AddRowButton } from "./RowActions";
import { useReorder } from "../lib/useReorder";
import { useSwipeToDelete } from "../lib/useSwipeToDelete";
import { useEditMode } from "../lib/EditModeContext";
import SourceBadge from "./SourceBadge";
import { toast } from "sonner";

const notifyTodoist = (res) => {
  if (!res?._todoist) return;
  if (res._todoist === "created") toast("Created in Todoist");
  else if (res._todoist === "updated") toast("Updated in Todoist");
  else if (res._todoist === "closed") toast("Closed in Todoist");
  else if (res._todoist === "reopened") toast("Reopened in Todoist");
  else if (res._todoist === "sync_failed") toast("Todoist sync failed", { description: "Local change kept." });
};

const undoDelete = (kind, snapshot, restoreFn) => (
  toast("Row deleted", {
    action: {
      label: "UNDO",
      onClick: async () => {
        try {
          // Recreate on backend with same fields (todoist link fresh on next edit)
          const { id: _, todoist_task_id: __, todoist_url: ___, ...body } = snapshot;
          await restoreFn(body);
          toast("Restored");
        } catch { toast("Undo failed"); }
      },
    },
    duration: 6000,
  })
);

const MoneyRow = ({ m, rowProps, handleProps, dragId, overId, load, remove, toggle, notifyTodoist }) => {
  const { swipeProps, swipeStyle, revealPct } = useSwipeToDelete(() => remove(m));
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-end pr-6 bg-[rgba(255,90,90,0.14)] text-[#FF7A7A] op-mono text-[10px] tracking-[0.24em] pointer-events-none" style={{ opacity: revealPct }}>
        DELETE
      </div>
      <div
        data-testid={`money-move-${m.id}`}
        {...rowProps(m.id)}
        {...swipeProps}
        style={swipeStyle}
        data-dragging={dragId === m.id}
        data-drop-active={overId === m.id}
        className={`op-editable-row grid grid-cols-12 gap-4 items-center px-6 py-5 transition-opacity duration-300 bg-[var(--op-panel-bg,rgba(10,12,20,1))] ${
          m.completed ? "opacity-40" : ""
        }`}
      >
        <div className="col-span-12 md:col-span-5">
          <div className="flex items-center gap-2 mb-1">
            <EditableText
              value={m.business}
              onSave={async (v) => { await api.patchMoneyMove(m.id, { business: v.toUpperCase() }); load(); }}
              className="op-tag"
              testid={`money-move-business-${m.id}`}
            />
            <SourceBadge source={m.source} />
          </div>
          <EditableText
            value={m.task}
            onSave={async (v) => { const res = await api.patchMoneyMove(m.id, { task: v }); notifyTodoist(res); load(); }}
            className={`text-white text-base font-medium block ${m.completed ? "line-through" : ""}`}
            multiline
            testid={`money-move-task-${m.id}`}
            emptyPlaceholder="Untitled money move…"
          />
        </div>
        <div className="col-span-6 md:col-span-3">
          <div className="op-tag mb-1">IMPACT</div>
          <EditableText
            value={m.impact}
            onSave={async (v) => { await api.patchMoneyMove(m.id, { impact: v }); load(); }}
            className="text-[#00E5FF] op-mono text-sm block"
            testid={`money-move-impact-${m.id}`}
          />
        </div>
        <div className="col-span-3 md:col-span-2">
          <div className="op-tag mb-1">DUE</div>
          <EditableText
            value={m.deadline || ""}
            onSave={async (v) => { await api.patchMoneyMove(m.id, { deadline: v }); load(); }}
            className="text-white op-mono text-sm block"
            emptyPlaceholder="—"
            testid={`money-move-deadline-${m.id}`}
          />
        </div>
        <div className="col-span-3 md:col-span-2 flex md:justify-end items-center gap-2">
          <RowActions onDelete={() => remove(m)} testid={`money-move-${m.id}`} handleProps={handleProps(m.id)} />
          <button
            onClick={() => toggle(m)}
            data-testid={`money-move-complete-${m.id}`}
            className="op-btn text-[10px] px-3 py-2"
          >
            {m.completed ? "REOPEN" : "DONE"}
          </button>
        </div>
      </div>
    </div>
  );
};

const MoneyMoves = () => {
  const [items, setItems] = useState([]);
  const { editMode } = useEditMode();
  const load = () => api.moneyMoves().then(setItems);
  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    const res = await api.patchMoneyMove(m.id, { completed: !m.completed });
    notifyTodoist(res);
    load();
  };

  const remove = async (m) => {
    setItems((prev) => prev.filter((x) => x.id !== m.id));
    try {
      await api.deleteMoneyMove(m.id);
      undoDelete("money-moves", m, async (body) => {
        const restored = await api.createMoneyMove(body);
        setItems((prev) => [...prev, restored]);
      });
    } catch { load(); }
  };

  const add = async () => {
    const created = await api.createMoneyMove({ task: "" });
    setItems((prev) => [...prev, created]);
  };

  const { rowProps, handleProps, dragId, overId } = useReorder(items, async (newIds) => {
    // Optimistic reorder
    setItems((prev) => newIds.map((id) => prev.find((p) => p.id === id)).filter(Boolean));
    try { await api.reorderMoneyMoves(newIds); } catch { load(); }
  });

  return (
    <section data-testid="money-moves-section">
      <SectionHeader
        code="02" label="MONEY MOVES"
        caption="Tasks directly connected to revenue."
        labelKey="section.money-moves.label"
        captionKey="section.money-moves.caption"
      />
      <div className="op-panel op-panel--glow p-0 overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#00E5FF]" aria-hidden="true" />
        <div className="divide-y divide-white/[0.05]">
          {items.map((m) => (
            <MoneyRow
              key={m.id}
              m={m}
              rowProps={rowProps}
              handleProps={handleProps}
              dragId={dragId}
              overId={overId}
              load={load}
              remove={remove}
              toggle={toggle}
              notifyTodoist={notifyTodoist}
            />
          ))}
        </div>
        {editMode && (
          <div className="px-6 py-4 border-t border-white/[0.05]">
            <AddRowButton onClick={add} label="ADD MONEY MOVE" testid="money-move-add" />
          </div>
        )}
      </div>
    </section>
  );
};

export default MoneyMoves;
